import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  Timestamp,
  serverTimestamp 
} from 'firebase/firestore';
import { getUserById, updateUserData, logAdminActivity } from '../firebase/helpers';
import { verifyAdminAccess } from './adminService';
import { resetUserProgress } from './dailyResetService';

/**
 * VIP Upgrade Requirements
 * NEW SYSTEM: Admin upgrades users and credits upgrade price to their balance
 */
export const VIP_UPGRADE_REQUIREMENTS = {
  'VIP1': { 
    nextLevel: 'VIP2', 
    upgradePrice: 500  // Amount credited to user when upgraded
  },
  'VIP2': { 
    nextLevel: 'VIP3', 
    upgradePrice: 1500  // Amount credited to user when upgraded
  },
  'VIP3': { 
    nextLevel: 'VIP4', 
    upgradePrice: 5000  // Amount credited to user when upgraded
  },
  'VIP4': { 
    nextLevel: null, // No higher level
    upgradePrice: null
  }
};

/**
 * Check if upgrade is possible (validation only - no balance requirement)
 * Admin can upgrade any user, and upgrade price is credited to user's balance
 */
export const checkVIPUpgradeEligibility = async (userId, targetVIP = null) => {
  try {
    const user = await getUserById(userId);
    if (!user) {
      return { eligible: false, reason: 'User not found' };
    }

    const currentVIP = user.vipLevel || 'VIP1';
    const walletBalance = user.balances?.wallet || 0;
    const commissionBalance = user.balances?.commission || 0;
    
    // Check if user is already at max level
    if (currentVIP === 'VIP4') {
      return { eligible: false, reason: 'Already at maximum VIP level' };
    }

    // If target VIP is specified, validate it
    if (targetVIP) {
      const getVipNumber = (vip) => parseInt(vip.replace('VIP', ''));
      const currentNum = getVipNumber(currentVIP);
      const targetNum = getVipNumber(targetVIP);

      // Cannot downgrade or set to same level
      if (targetNum <= currentNum) {
        return { eligible: false, reason: 'Cannot downgrade or set to same level' };
      }

      // Cannot skip levels (must upgrade one level at a time)
      if (targetNum !== currentNum + 1) {
        return { eligible: false, reason: 'Cannot skip levels. Must upgrade one level at a time.' };
      }
    }

    const upgradeInfo = VIP_UPGRADE_REQUIREMENTS[currentVIP];
    if (!upgradeInfo || !upgradeInfo.nextLevel) {
      return { eligible: false, reason: 'No upgrade available' };
    }

    const upgradePrice = upgradeInfo.upgradePrice;
    const nextVIP = targetVIP || upgradeInfo.nextLevel;

    // Check if user has sufficient balance for the upgrade
    // (Even though admin credits the amount, user must have enough balance to qualify)
    if (walletBalance < upgradePrice) {
      return {
        eligible: false,
        reason: 'Insufficient balance',
        currentVIP,
        nextVIP,
        upgradePrice,
        currentBalance: walletBalance,
        needed: upgradePrice - walletBalance
      };
    }

    // Calculate new balance (upgrade price is ADDED, not deducted)
    const newWalletBalance = walletBalance + upgradePrice;
    const newTotalBalance = newWalletBalance + commissionBalance;

    return {
      eligible: true,
      currentVIP,
      nextVIP,
      upgradePrice,
      currentBalance: walletBalance,
      newBalance: newWalletBalance,
      newTotalBalance
    };
  } catch (error) {
    console.error('Error checking VIP upgrade eligibility:', error);
    return { eligible: false, error: error.message };
  }
};

/**
 * Process VIP upgrade - ADMIN ONLY
 * NEW SYSTEM: Upgrade price is ADDED to user's balance (not deducted)
 * 
 * Validation:
 * - Only admin can upgrade
 * - Cannot skip levels (must upgrade one at a time)
 * - Cannot downgrade
 * - Must be authenticated admin
 * 
 * @param {string} userId - User ID to upgrade
 * @param {string} targetVIP - Target VIP level (optional, defaults to next level)
 * @param {string} adminId - Admin user ID (required)
 * @param {string} adminEmail - Admin email (required)
 * @returns {Promise<Object>} Upgrade result
 */
export const processVIPUpgrade = async (userId, targetVIP = null, adminId = null, adminEmail = null) => {
  try {
    // Admin is required
    if (!adminId || !adminEmail) {
      throw new Error('Admin authentication required. Only admins can upgrade users.');
    }

    const user = await getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    const currentVIP = user.vipLevel || 'VIP1';
    const walletBalance = user.balances?.wallet || 0;
    const commissionBalance = user.balances?.commission || 0;

    // Determine target VIP level
    let nextVIP = targetVIP;
    if (!nextVIP) {
      const upgradeInfo = VIP_UPGRADE_REQUIREMENTS[currentVIP];
      if (!upgradeInfo || !upgradeInfo.nextLevel) {
        throw new Error('No upgrade available. User is already at maximum VIP level.');
      }
      nextVIP = upgradeInfo.nextLevel;
    }

    // Validate upgrade (cannot skip levels, cannot downgrade)
    const getVipNumber = (vip) => parseInt(vip.replace('VIP', ''));
    const currentNum = getVipNumber(currentVIP);
    const targetNum = getVipNumber(nextVIP);

    if (targetNum <= currentNum) {
      throw new Error('Cannot downgrade or set to same VIP level');
    }

    if (targetNum !== currentNum + 1) {
      throw new Error('Cannot skip levels. Must upgrade one level at a time.');
    }

    // Get upgrade price for this specific upgrade
    const upgradeInfo = VIP_UPGRADE_REQUIREMENTS[currentVIP];
    if (!upgradeInfo || upgradeInfo.nextLevel !== nextVIP) {
      throw new Error(`Invalid upgrade path: ${currentVIP} → ${nextVIP}`);
    }

    const upgradePrice = upgradeInfo.upgradePrice;

    // Calculate new balance (ADD upgrade price, not deduct)
    const newWalletBalance = walletBalance + upgradePrice;
    const newTotalBalance = newWalletBalance + commissionBalance;

    // Update user VIP level and balance (atomic operation)
    await updateUserData(userId, {
      vipLevel: nextVIP,
      balances: {
        ...user.balances,
        wallet: newWalletBalance,
        total: newTotalBalance,
        previousVIP: currentVIP,
        upgradedAt: Timestamp.now(),
        upgradePriceCredited: upgradePrice
      },
      updatedAt: Timestamp.now()
    });

    // Record upgrade transaction in vipUpgrades collection (history log)
    const upgradeRef = doc(collection(db, 'vipUpgrades'));
    const upgradeId = upgradeRef.id;
    
    await setDoc(upgradeRef, {
      id: upgradeId,
      userId: userId,
      userEmail: user.email || '',
      fromVIP: currentVIP,
      toVIP: nextVIP,
      upgradePrice: upgradePrice,  // Amount credited
      previousBalance: walletBalance,
      newBalance: newWalletBalance,
      previousTotalBalance: walletBalance + commissionBalance,
      newTotalBalance: newTotalBalance,
      processedBy: adminId,
      processedByEmail: adminEmail,
      processedAt: Timestamp.now(),
      createdAt: serverTimestamp(),
      type: 'admin_upgrade'  // Mark as admin upgrade
    });

    // Log admin activity
    await logAdminActivity({
      adminId: adminId,
      adminEmail: adminEmail,
      action: 'upgradeUserVIP',
      targetUserId: userId,
      targetUserEmail: user.email,
      changes: {
        fromVIP: currentVIP,
        toVIP: nextVIP,
        upgradePriceCredited: upgradePrice,
        previousBalance: walletBalance,
        newBalance: newWalletBalance
      },
      description: `Admin upgraded user ${user.email || userId} from ${currentVIP} to ${nextVIP}. ${upgradePrice} USDT credited to user's balance. Progress reset to zero.`
    });

    // Reset all optimization progress and task completion statistics
    // This happens on VIP upgrade to give user a fresh start with new VIP level
    try {
      console.log(`🔄 Resetting optimization progress for user ${userId} due to VIP upgrade from ${currentVIP} to ${nextVIP}`);
      await resetUserProgress(userId, nextVIP);
      
      // Also reset today's daily profit statistics
      const getTodayKey = () => {
        const now = new Date();
        return now.toISOString().split('T')[0]; // Returns YYYY-MM-DD
      };
      const todayKey = getTodayKey();
      const dailyProfitRef = doc(db, 'dailyProfits', `${userId}_${todayKey}`);
      const dailyProfitDoc = await getDoc(dailyProfitRef);
      
      if (dailyProfitDoc.exists()) {
        await updateDoc(dailyProfitRef, {
          totalProfit: 0,
          tasksCompleted: 0,
          taskBreakdown: [],
          updatedAt: serverTimestamp()
        });
        console.log(`✅ Reset today's daily profit statistics for user ${userId}`);
      }
      
      console.log(`✅ Successfully reset optimization progress and task statistics for user ${userId}`);
    } catch (resetError) {
      console.error('Error resetting user progress on VIP upgrade:', resetError);
      // Don't fail the upgrade if reset fails, but log it
      // The upgrade is still successful even if progress reset fails
    }

    // Create success notification for user
    try {
      const notificationRef = doc(collection(db, 'notifications'));
      await setDoc(notificationRef, {
        userId: userId,
        type: 'success',
        title: '🎉 VIP Upgrade Successful!',
        message: `Congratulations! You have been upgraded to ${nextVIP}. ${upgradePrice} USDT has been credited to your wallet balance. Your optimization progress has been reset to start fresh with your new VIP level.`,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (notifError) {
      console.error('Error creating notification:', notifError);
    }

    console.log(`✅ VIP upgrade successful: ${userId} from ${currentVIP} to ${nextVIP}. ${upgradePrice} USDT credited.`);

    return {
      success: true,
      fromVIP: currentVIP,
      toVIP: nextVIP,
      upgradePrice,
      previousBalance: walletBalance,
      newBalance: newWalletBalance,
      upgradeId
    };
  } catch (error) {
    console.error('Error processing VIP upgrade:', error);
    throw error;
  }
};

/**
 * Get user's upgrade history
 */
export const getUserUpgradeHistory = async (userId) => {
  try {
    const upgradesRef = collection(db, 'vipUpgrades');
    const q = query(
      upgradesRef,
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);

    const upgrades = [];
    snapshot.forEach((doc) => {
      upgrades.push(doc.data());
    });

    // Sort by date (newest first)
    upgrades.sort((a, b) => {
      const timeA = a.processedAt?.toDate?.()?.getTime() || 0;
      const timeB = b.processedAt?.toDate?.()?.getTime() || 0;
      return timeB - timeA;
    });

    return upgrades;
  } catch (error) {
    console.error('Error getting upgrade history:', error);
    return [];
  }
};

/**
 * Check if user has seen upgrade popup today
 */
export const hasSeenUpgradePopupToday = async (userId, targetVIP) => {
  try {
    const user = await getUserById(userId);
    if (!user) return false;

    const todayKey = new Date().toISOString().split('T')[0];
    const seenPopups = user.seenUpgradePopups || {};
    const popupKey = `vip${targetVIP}_${todayKey}`;

    return seenPopups[popupKey] === true;
  } catch (error) {
    console.error('Error checking seen popup:', error);
    return false;
  }
};

/**
 * Mark upgrade popup as seen
 */
export const markUpgradePopupSeen = async (userId, targetVIP, reminderOption = null) => {
  try {
    const user = await getUserById(userId);
    if (!user) return;

    const todayKey = new Date().toISOString().split('T')[0];
    const seenPopups = user.seenUpgradePopups || {};
    const popupKey = `vip${targetVIP}_${todayKey}`;

    seenPopups[popupKey] = {
      seen: true,
      seenAt: Timestamp.now(),
      reminderOption: reminderOption || 'none'
    };

    await updateUserData(userId, {
      seenUpgradePopups: seenPopups,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error marking popup as seen:', error);
  }
};

/**
 * Get VIP upgrade benefits comparison
 */
export const getVIPBenefits = (fromVIP, toVIP) => {
  const VIP_CONFIG = {
    'VIP1': { sets: 3, tasksPerSet: 40, totalTasks: 120, commission: 0.4 },
    'VIP2': { sets: 4, tasksPerSet: 45, totalTasks: 180, commission: 0.6 },
    'VIP3': { sets: 5, tasksPerSet: 50, totalTasks: 250, commission: 0.8 },
    'VIP4': { sets: 6, tasksPerSet: 55, totalTasks: 330, commission: 1.0 }
  };

  const fromConfig = VIP_CONFIG[fromVIP] || VIP_CONFIG['VIP1'];
  const toConfig = VIP_CONFIG[toVIP] || VIP_CONFIG['VIP1'];

  return {
    from: {
      vip: fromVIP,
      sets: fromConfig.sets,
      tasksPerSet: fromConfig.tasksPerSet,
      totalTasks: fromConfig.totalTasks,
      commission: fromConfig.commission
    },
    to: {
      vip: toVIP,
      sets: toConfig.sets,
      tasksPerSet: toConfig.tasksPerSet,
      totalTasks: toConfig.totalTasks,
      commission: toConfig.commission
    },
    improvements: {
      setsIncrease: toConfig.sets - fromConfig.sets,
      tasksPerSetIncrease: toConfig.tasksPerSet - fromConfig.tasksPerSet,
      totalTasksIncrease: toConfig.totalTasks - fromConfig.totalTasks,
      commissionIncrease: toConfig.commission - fromConfig.commission,
      earningPotentialIncrease: ((toConfig.totalTasks - fromConfig.totalTasks) / fromConfig.totalTasks * 100).toFixed(0)
    }
  };
};

